import React, { useState, useMemo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import qapi from "views_shared/app/javascript/api";
import { throwOnApiError } from "views_shared/app/javascript/api/api";
import { deserializeModel as deserializeEmployment } from "../../pr_profiles/edit/employment_modal_body/serialization";

const ExpertContext = React.createContext(null);

// The provider's collections are stored DESERIALIZED (camelCase), the shape the PR page's edit-modal
// serializers established, so the show sections and the edit surface read one shape. Employment reuses
// the PR serializer outright; these two collections have no serializer of their own, so their read
// mappings live here. The `expert` record itself stays the raw PostgREST row.
const representationMapping = {
  id: "id",
  promotedCompanyMigrateId: "promoted_company_migrate_id",
  promotedCompanyName: "promoted_company_name",
  promotedCompanySlug: "promoted_company_slug",
  promotedEntityId: "promoted_entity_id",
  promotedEntityMigrateId: "promoted_entity_migrate_id",
  promotedEntityName: "promoted_entity_name",
  promotedEntityProfileImageUrl: "promoted_entity_profile_image_url",
  promotedEntitySlug: "promoted_entity_slug",
  promotedEntityType: "promoted_entity_type",
  promotedExpertJobTitle: "promoted_expert_job_title",
  selfExpert: "self_expert",
};

// These rows come from the pr_profiles view, which names its job columns title/company_name/
// company_slug — there is no featured_* column on it, so mapping those names yielded undefined and
// the "Represented by" rows rendered no subtitle at all.
const representativeMapping = {
  id: "id",
  companyMigrateId: "company_migrate_id",
  companyName: "company_name",
  companySlug: "company_slug",
  migrateId: "migrate_id",
  name: "name",
  profileImageUrl: "profile_image_url",
  slug: "slug",
  title: "title",
};

function deserializeWith(mapping) {
  return (row) =>
    Object.keys(mapping).reduce((acc, key) => {
      acc[key] = row[mapping[key]];
      return acc;
    }, {});
}

const deserializeRepresentation = deserializeWith(representationMapping);
const deserializeRepresentative = deserializeWith(representativeMapping);

// getResource RESOLVES with `{ error }` on a non-2xx — only a transport failure rejects. Reading
// `response.data` straight through would therefore turn a failed read into an empty collection and
// wipe the section; throwing instead leaves the collection at its previous value on a nonfatal reload.
function readRows(response) {
  throwOnApiError(response);
  return Array.isArray(response.data) ? response.data : [];
}

// Dedupe derived tags by hashtag_id with a times_tagged count, most-tagged first (same shaping as the
// PR/Media providers).
function dedupeTags(rows) {
  return rows
    .reduce((acc, current) => {
      const existing = acc.find((item) => item.hashtag_id === current.hashtag_id);
      if (existing) {
        existing.times_tagged += 1;
      } else {
        acc.push({ ...current, times_tagged: 1 });
      }
      return acc;
    }, [])
    .sort((a, b) => b.times_tagged - a.times_tagged);
}

// Shape the batched derived-tags read into `{ promoEntityId: [tag, ...] }` for the represents rows.
// The view fans one hashtag out per tagged object and per representing profile, so an entity's rows
// arrive duplicated by construction — hence the same dedupe/ranking the expertise chips get, applied
// per entity, so a row showing only its top few chips shows the top few rather than whichever ones
// PostgREST happened to return first. Reads the response directly rather than through readRows:
// chips are enrichment, and a failed read must leave the section alone.
function groupTagsByEntity(response) {
  const rows = Array.isArray(response.data) ? response.data : [];
  const byEntity = new Map();

  rows.forEach((row) => {
    byEntity.set(row.promo_entity_id, [...(byEntity.get(row.promo_entity_id) || []), row]);
  });

  return Object.fromEntries([...byEntity].map(([entityId, entityRows]) => [entityId, dedupeTags(entityRows)]));
}

// The representations view fans out one row per job join, so an entity can appear several times.
// Dedupe by promotedEntityId, preferring rows with richer data (a job title for experts; company
// info for anything). Harvested from the retired prototype — this is the page's one piece of
// non-obvious data shaping.
//
// Each deduped entity also keeps EVERY fan-out row's representation id in `representationIds`
// (normally one — a promoter represents a given entity once). Removing an entity destroys all of
// them, so the display row that wins the richness comparison must not take its own id with it and
// drop the others: the ids accumulate independently of which row is displayed.
function dedupeRepresentedEntities(rows) {
  const entityMap = new Map();

  rows.forEach((entity) => {
    const entityId = entity.promotedEntityId;
    const existing = entityMap.get(entityId);

    if (!existing) {
      entityMap.set(entityId, { ...entity, representationIds: [entity.id] });
      return;
    }

    if (!existing.representationIds.includes(entity.id)) existing.representationIds.push(entity.id);

    const richerJobTitle =
      entity.promotedEntityType === "Id::PromoEntity::Expert" &&
      entity.promotedExpertJobTitle &&
      !existing.promotedExpertJobTitle;
    const richerCompany = entity.promotedCompanyName && !existing.promotedCompanyName;

    if (richerJobTitle || richerCompany) {
      entityMap.set(entityId, { ...entity, representationIds: existing.representationIds });
    }
  });

  return Array.from(entityMap.values());
}

// Expert's state provider — the shared provider/context pattern (see profiles/README.md). Owns the
// expert record, expertise hashtags, employment (company jobs), the PR reps who represent this expert
// (representedBy), and everything this expert's own promoter side represents (alsoRepresents — which
// drives hybrid detection). It also carries the edit-state slots the PR/Media providers established
// (openEditModal, isUpdating, generalError) so the edit surface reads them from one place.
function ExpertProvider(props) {
  const { children } = props;

  // eslint-disable-next-line react/destructuring-assignment
  const { viewerContext = {} } = props.value;

  // alphabetized
  // A tab key the section should switch to once (e.g. after a successful add from a different tab);
  // consumed and cleared by RepresentsSection's own effect via onActiveTabRequestHandled. Lives next to
  // openEditModal — the edit modals and the show-page sections are siblings, so this is the rail they
  // already share for exactly this shape of state.
  const [activeTabRequest, setActiveTabRequest] = useState(null);
  const [alsoRepresents, setAlsoRepresents] = useState([]);
  const [asyncError, setAsyncError] = useState(null);
  const [companyJobs, setCompanyJobs] = useState([]);
  const [expert, setExpert] = useState(null);
  const [expertId, setExpertId] = useState(null);
  const [generalError, setGeneralError] = useState(null);
  const [hashtags, setHashtags] = useState([]);
  // The headshot has no edit modal, so the "Add a headshot" checklist row opens the header avatar's
  // file picker by bumping this nonce (watched in expert_person_header.jsx).
  const [headshotPickerRequest, setHeadshotPickerRequest] = useState(0);
  const requestHeadshotPicker = useCallback(() => setHeadshotPickerRequest((count) => count + 1), []);
  // The Quotes section's add editor is inline (no modal), so the "Add a quote" checklist row opens it
  // by bumping this nonce (watched in expert_sections.jsx, which also scrolls the section into view).
  const [addQuoteRequest, setAddQuoteRequest] = useState(0);
  const requestAddQuote = useCallback(() => setAddQuoteRequest((count) => count + 1), []);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(null);
  const [representedBy, setRepresentedBy] = useState([]);
  const [representedExpertTags, setRepresentedExpertTags] = useState({});

  // Hybrid = this expert's promoter side also represents OTHER experts (not just companies/products).
  const hybrid = useMemo(
    () => alsoRepresents.some((entity) => entity.promotedEntityType === "Id::PromoEntity::Expert"),
    [alsoRepresents],
  );

  // The promoter profile that self-represents this expert: the promoter side that OWNS everything in
  // alsoRepresents, and so the profile a new representation is created against. Threaded from here
  // rather than re-derived inside the mutation hook, which has no way to tell this id apart from the
  // co-representing PR profiles the page also carries (representedBy).
  const selfProfileId = expert?.self_represented_profile_id ?? null;

  // The full data load. `silent` skips the page-level loading flag so a save-triggered refetch doesn't
  // flash the skeleton; `nonfatal` keeps a failed refetch off the page error boundary (same split as
  // the PR/Media providers).
  //
  // Resolves with the freshly read expert row (or null when there is none / the load failed). The save
  // path needs that row: a rename resets the expert's slug server-side, and the page has to follow the
  // new one. Returning it here means the post-save refresh doubles as that read instead of costing a
  // second round trip.
  const loadProfile = useCallback(
    async ({ silent = false, nonfatal = false } = {}) => {
      if (!expertId) return null;
      try {
        if (!silent) setIsLoading(true);

        // The expert record first — the dependent reads key off its identity columns.
        const expertResponse = await qapi.experts.get(expertId);
        const expertRows = readRows(expertResponse);
        const e = expertRows.length ? expertRows[0] : null;
        setExpert(e);
        if (!e) return null;

        const representativeIds = Array.isArray(e.representative_profile_ids) ? e.representative_profile_ids : [];
        // The promoter profile that self-represents this expert, when there is one. Profile slugs and
        // PromoEntity slugs are independently unique, so an unrelated promoter can carry this expert's
        // slug — both relationship reads key off this id rather than matching slugs. (Named to stay
        // distinct from the provider-level `selfProfileId`, which derives from the expert STATE and is
        // one render behind this freshly loaded value.)
        const loadedSelfProfileId = e.self_represented_profile_id;

        const representedByPromise = representativeIds.length
          ? qapi.prProfiles.getAllById(representativeIds).then((response) => {
              // A self-representing expert appears among its own representatives; show only the others.
              const rows = readRows(response).map(deserializeRepresentative);
              setRepresentedBy(loadedSelfProfileId ? rows.filter((rep) => rep.id !== loadedSelfProfileId) : rows);
            })
          : Promise.resolve(setRepresentedBy([]));

        // Without a self-representing promoter profile there is no promoter side to represent anything,
        // so the feed is empty and the profile is not hybrid.
        // Chips for the represents rows. Only the Experts tab shows them, so only that subset is
        // asked for — and an empty subset resolves without issuing a request at all. Enrichment, so
        // it soft-fails to no chips rather than rejecting the read the whole section depends on.
        const loadRepresentedExpertTags = (entities) =>
          qapi.hashtags
            .getAllForPromoEntitiesForDisplay(
              entities
                .filter((entity) => entity.promotedEntityType === "Id::PromoEntity::Expert")
                .map((entity) => entity.promotedEntityId),
              "Id::PromoEntity::Expert",
            )
            .then((tagsResponse) => setRepresentedExpertTags(groupTagsByEntity(tagsResponse)))
            .catch(() => setRepresentedExpertTags({}));

        const alsoRepresentsPromise = loadedSelfProfileId
          ? qapi.representations.getAllByPromoterProfileId(loadedSelfProfileId).then((response) => {
              const representations = readRows(response)
                .map(deserializeRepresentation)
                .filter((rep) => !rep.selfExpert);
              const entities = dedupeRepresentedEntities(representations);
              setAlsoRepresents(entities);
              // Chained onto this read rather than fired from an effect on the collection: the ids
              // come from THIS response (state has not committed yet), and returning the promise puts
              // the chips inside the same barrier below, so a reload cannot resolve before its own
              // chips have landed.
              return loadRepresentedExpertTags(entities);
            })
          : Promise.resolve(setAlsoRepresents([])).then(() => loadRepresentedExpertTags([]));

        const jobsPromise = qapi.companyJobs.getAllByExpert(expertId).then((response) => {
          setCompanyJobs(readRows(response).map(deserializeEmployment));
        });

        // Tags are enrichment, not page-critical, so this read soft-fails to an empty list either way
        // — it deliberately does not go through readRows.
        const tagsPromise = qapi.hashtags
          .getAllExpertDerivedTags(e.slug)
          .then((response) => {
            const rows = Array.isArray(response.data) ? response.data : [];
            setHashtags(dedupeTags(rows));
          })
          .catch(() => setHashtags([]));

        if (nonfatal) {
          // Callers treat resolution as "all state writes are done", so every started read must settle
          // before loadProfile resolves — a read left in flight could commit stale state over a later
          // retry's. Settle all four, then rethrow the first rejection for the catch below.
          const results = await Promise.allSettled([
            representedByPromise,
            alsoRepresentsPromise,
            jobsPromise,
            tagsPromise,
          ]);
          const rejected = results.find((r) => r.status === "rejected");
          if (rejected) throw rejected.reason;
        } else {
          // Fatal loads fail fast to the error boundary rather than idling on the skeleton behind a
          // stalled sibling read (the fetch layer has no timeout); teardown means no retry to race.
          await Promise.all([representedByPromise, alsoRepresentsPromise, jobsPromise, tagsPromise]);
        }

        return e;
      } catch (error) {
        // A nonfatal reload is used when reconciling after a failed save — a failed refresh there must
        // not escalate a recoverable modal error into the page error boundary (asyncError is re-thrown).
        if (!nonfatal) setAsyncError(error);
        return null;
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [expertId],
  );

  useEffect(() => {
    if (expertId) loadProfile();
  }, [expertId, loadProfile]);

  // Silent refetch the edit surface calls after a save. Forwards options so a caller can mark the
  // reload nonfatal (e.g. reconciling after a failed save); default call sites are unaffected. Also
  // forwards loadProfile's resolved expert row, which the save path uses to follow a reset slug.
  const silentReload = useCallback((opts = {}) => loadProfile({ silent: true, ...opts }), [loadProfile]);

  const providerValue = useMemo(
    () => ({
      // alphabetized
      activeTabRequest,
      addQuoteRequest,
      alsoRepresents,
      asyncError,
      companyJobs,
      expert,
      expertId,
      generalError,
      hashtags,
      headshotPickerRequest,
      hybrid,
      isLoading,
      isUpdating,
      openEditModal,
      representedBy,
      representedExpertTags,
      requestAddQuote,
      requestHeadshotPicker,
      selfProfileId,
      setActiveTabRequest,
      setAlsoRepresents,
      setAsyncError,
      setCompanyJobs,
      setExpertId,
      setGeneralError,
      setIsUpdating,
      setOpenEditModal,
      silentReload,
      viewerContext,
    }),
    [
      activeTabRequest,
      addQuoteRequest,
      alsoRepresents,
      asyncError,
      companyJobs,
      expert,
      expertId,
      generalError,
      hashtags,
      headshotPickerRequest,
      hybrid,
      isLoading,
      isUpdating,
      openEditModal,
      representedBy,
      representedExpertTags,
      requestAddQuote,
      requestHeadshotPicker,
      selfProfileId,
      silentReload,
      viewerContext,
    ],
  );

  return <ExpertContext.Provider value={providerValue}>{children}</ExpertContext.Provider>;
}

export { ExpertContext, ExpertProvider };

ExpertProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};
